import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate } from "@/lib/api";
import { PageHeader, GlassCard } from "@/components/Primitives";
import { ChevronRight } from "lucide-react";

const SECTIONS = [
  ["planning", "Planning"], ["design", "Design"], ["development", "Development"],
  ["content", "Content"], ["seo", "SEO"], ["testing", "Testing"],
];

function Bar({ value }) {
  return (
    <div className="h-1 rounded-full bg-white/[0.05] overflow-hidden">
      <div className="h-full rounded-full bg-gradient-to-r from-[#007AFF] to-[#5AC8FA] transition-all" style={{ width: `${value}%` }} />
    </div>
  );
}

export default function Projects() {
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    (async () => { const { data } = await api.get("/projects"); setProjects(data); })();
  }, []);

  return (
    <div data-testid="projects-page">
      <PageHeader title="Projects" subtitle="Every build, every milestone, every percentage point." testId="projects-header" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {projects.map((p) => (
          <Link key={p.id} to={`/projects/${p.id}`} className="block group" data-testid={`project-${p.id}`}>
            <GlassCard className="group-hover:-translate-y-0.5 group-hover:border-[#007AFF]/30 transition cursor-pointer">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#007AFF]/70">{p.project_type}</div>
                  <div className="font-display text-xl text-white mt-1 flex items-center gap-2">
                    {p.project_name}
                    <ChevronRight className="w-4 h-4 text-white/30 group-hover:text-[#007AFF] transition" />
                  </div>
                  <div className="text-xs text-white/45 mt-1">{p.client_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">Value</div>
                  <div className="font-display text-lg">{fmtMoney(p.estimated_value)}</div>
                </div>
              </div>

              <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-[0.22em] text-white/40">Overall</div>
                <div className="text-sm font-medium">{p.overall_progress}%</div>
              </div>
              <Bar value={p.overall_progress} />

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-5 gap-y-3 mt-5">
                {SECTIONS.map(([k, label]) => (
                  <div key={k}>
                    <div className="flex justify-between text-[11px] mb-1">
                      <span className="text-white/55">{label}</span>
                      <span className="text-white/40">{p.progress?.[k] || 0}%</span>
                    </div>
                    <Bar value={p.progress?.[k] || 0} />
                  </div>
                ))}
              </div>

              <div className="flex items-center justify-between mt-5 pt-4 border-t border-white/[0.05] text-xs text-white/45">
                <span>Start {fmtDate(p.start_date)}</span>
                <span>Target {fmtDate(p.target_completion_date)}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${
                  p.status === "completed" ? "bg-emerald-400/10 text-emerald-300 border border-emerald-400/20" :
                  p.status === "review" ? "bg-violet-400/10 text-violet-300 border border-violet-400/20" :
                  p.status === "deployed" ? "bg-cyan-400/10 text-cyan-300 border border-cyan-400/20" :
                  "bg-blue-400/10 text-blue-300 border border-blue-400/20"
                }`}>{p.status}</span>
              </div>
            </GlassCard>
          </Link>
        ))}
        {projects.length === 0 && <GlassCard className="col-span-full text-center text-white/40">No projects yet.</GlassCard>}
      </div>
    </div>
  );
}
